/**
 * Admin Analytics Tab - View site statistics and export data
 */
import { toast } from "sonner";
import { 
  Cat, Calendar, Phone, CheckCircle, Users, Mail, Star, 
  ShoppingBag, TrendingUp, FileText, Download 
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { useAdmin } from "./AdminContext";
import SessionAnalyticsCard from "../../components/SessionAnalyticsCard";

const AdminAnalyticsTab = () => {
  const { analytics, trends } = useAdmin();

  const handleExportCSV = () => {
    if (!analytics) return;
    const csvData = [
      ['Metric', 'Value'],
      ['Total Cats', analytics.cats?.total || 0],
      ['Available Cats', analytics.cats?.available || 0],
      ['Reserved Cats', analytics.cats?.reserved || 0],
      ['Adopted Cats', analytics.cats?.adopted || 0],
      ['Total Applications', analytics.adoptions?.total_applications || 0],
      ['Pending Applications', analytics.adoptions?.pending || 0],
      ['Waiting List', analytics.adoptions?.general_interest_waiting_list || 0],
      ['Total Bookings', analytics.bookings?.total || 0],
      ['Total Donations', analytics.donations?.total_count || 0],
      ['Donation Amount', '£' + (analytics.donations?.total_amount?.toFixed(2) || 0)],
      ['Last 30 Days Donations', '£' + (analytics.donations?.last_30_days_amount?.toFixed(2) || 0)],
      ['Shop Orders', analytics.shop?.total_orders || 0],
      ['Shop Revenue', '£' + (analytics.shop?.total_revenue?.toFixed(2) || 0)],
      ['Approved Volunteers', analytics.volunteers?.approved || 0],
      ['Newsletter Subscribers', analytics.newsletter?.active_subscribers || 0],
      ['Total Reviews', analytics.reviews?.total || 0],
      ['Total Messages', analytics.messages?.total || 0],
    ];
    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `catpals-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Analytics exported to CSV');
  };

  return (
    <div className="grid gap-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={handleExportCSV}>
          <Download className="w-4 h-4 mr-2" /> Export to CSV
        </Button>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Cat className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-xl font-bold">{analytics?.cats?.total || 0}</p>
            <p className="text-xs text-stone-500">Total Cats</p>
            <p className="text-xs text-green-600">{analytics?.cats?.available || 0} available</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-xl font-bold">{analytics?.cats?.adopted || 0}</p>
            <p className="text-xs text-stone-500">Cats Adopted</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <FileText className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-xl font-bold">{analytics?.adoptions?.total_applications || 0}</p>
            <p className="text-xs text-stone-500">Applications</p>
            <p className="text-xs text-amber-600">{analytics?.adoptions?.pending || 0} pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Calendar className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-xl font-bold">{analytics?.bookings?.total || 0}</p>
            <p className="text-xs text-stone-500">Bookings</p>
            <p className="text-xs text-amber-600">{analytics?.bookings?.pending || 0} pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-xl font-bold">£{analytics?.donations?.total_amount?.toFixed(0) || 0}</p>
            <p className="text-xs text-stone-500">Total Donations</p>
            <p className="text-xs text-green-600">£{analytics?.donations?.last_30_days_amount?.toFixed(0) || 0} (30d)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <ShoppingBag className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-xl font-bold">£{analytics?.shop?.total_revenue?.toFixed(0) || 0}</p>
            <p className="text-xs text-stone-500">Shop Revenue</p>
            <p className="text-xs text-stone-400">{analytics?.shop?.total_orders || 0} orders</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Volunteers</p>
                <p className="text-2xl font-bold">{analytics?.volunteers?.approved || 0}</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-xs text-amber-600 mt-1">{analytics?.volunteers?.pending || 0} pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Newsletter</p>
                <p className="text-2xl font-bold">{analytics?.newsletter?.active_subscribers || 0}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-500" />
            </div>
            <p className="text-xs text-stone-400 mt-1">active subscribers</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Reviews</p>
                <p className="text-2xl font-bold">{analytics?.reviews?.total || 0}</p>
              </div>
              <Star className="w-8 h-8 text-amber-500" />
            </div>
            <p className="text-xs text-amber-600 mt-1">{analytics?.reviews?.pending || 0} pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Messages</p>
                <p className="text-2xl font-bold">{analytics?.messages?.total || 0}</p>
              </div>
              <Phone className="w-8 h-8 text-purple-500" />
            </div>
            <p className="text-xs text-red-600 mt-1">{analytics?.messages?.unread || 0} unread</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity (Last 7 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-stone-50 rounded-lg">
              <p className="text-3xl font-bold text-amber-600">{analytics?.recent_activity?.adoption_applications_7d || 0}</p>
              <p className="text-sm text-stone-600">New Applications</p>
            </div>
            <div className="text-center p-4 bg-stone-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">{analytics?.recent_activity?.bookings_7d || 0}</p>
              <p className="text-sm text-stone-600">New Bookings</p>
            </div>
            <div className="text-center p-4 bg-stone-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">{analytics?.recent_activity?.volunteer_applications_7d || 0}</p>
              <p className="text-sm text-stone-600">Volunteer Apps</p>
            </div>
            <div className="text-center p-4 bg-stone-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{analytics?.recent_activity?.newsletter_signups_7d || 0}</p>
              <p className="text-sm text-stone-600">Newsletter Signups</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Waiting List Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Adoption Waiting List</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="text-center p-4 bg-amber-50 rounded-lg flex-1">
              <p className="text-3xl font-bold text-amber-600">{analytics?.adoptions?.general_interest_waiting_list || 0}</p>
              <p className="text-sm text-stone-600">People registered for general adoption interest</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session/Login Analytics */}
      <SessionAnalyticsCard />
    </div>
  );
};

export default AdminAnalyticsTab;
